import React, { useMemo, useState, useEffect, useRef } from "react";
import { Card, Typography, Box } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useDashboardData } from '../../../contexts/DashboardDataContext';
import { useDashboardContext } from '../../Dashboard';

// Wrap CustomTooltip in memo to prevent unnecessary rerenders
const CustomTooltip = React.memo(({ active, payload, label }) => {
    if (active && payload && payload.length) {
        // Calculate total queue if queue segments exist
        const queueSegments = payload.filter(p => p.dataKey.startsWith('queue_'));
        const totalQueue = queueSegments.reduce((sum, item) => sum + item.value, 0);
        
        return (
            <div style={{ backgroundColor: '#444', padding: '10px', borderRadius: '5px' }}>
                {queueSegments.length > 0 && (
                    <p style={{ color: '#fff', margin: '0 0 5px 0' }}>
                        <strong>Service Queue: {totalQueue}</strong>
                    </p>
                )}
                {payload.map((entry, index) => {
                    const name = entry.name.replace(/ \(.*\)$/, '');
                    return (
                        <p key={index} style={{ color: entry.color, margin: '0' }}>
                            {name}: {entry.value}
                        </p>
                    );
                })}
            </div>
        );
    }
    return null;
});

const DailyProgressWidget = () => {
    const { dailyProgressData, fetchDailyProgress, getCachedData } = useDashboardData();
    const { currentZone } = useDashboardContext();
    const { loading, data, error } = dailyProgressData;
    const [refreshing, setRefreshing] = useState(false);
    const initialized = useRef(false);

    // Get cached data if available
    const cachedData = useMemo(() => {
        return getCachedData?.('dailyProgress') || null;
    }, [getCachedData]);
    
    // Extract and transform the API data into chart-friendly format
    const processedChartData = useMemo(() => {
        const rawData = data || cachedData;
        console.log('[DailyProgressWidget] Raw data received:', rawData);
        
        if (rawData && typeof rawData === 'object') {
            // Handle the flat data structure from /activity/daily-summary
            return {
                chartData: [
                    {
                        label: 'Status',
                        newToday: rawData.newToday || 0,
                        queue_0_3_days: rawData.queue_0_3_days || 0,
                        queue_3_5_days: rawData.queue_3_5_days || 0,
                        queue_over_5_days: rawData.queue_over_5_days || 0,
                        startedToday: rawData.startedToday || 0,
                        // Add totalQueue for debugging
                        totalQueue: rawData.totalQueue || 0
                    }
                ],
                isSamsungUser: false
            };
        }
        
        // Default fallback
        return { chartData: [], isSamsungUser: false };
    }, [data, cachedData]);

    // Enhanced effect to smoothly handle data refresh
    useEffect(() => {
        // If we have no data and are not loading or in error, fetch once
        if (!data && !loading && !error && !initialized.current) {
            console.log('[DailyProgressWidget] No data, triggering initial fetch...');
            fetchDailyProgress(true);
            initialized.current = true;
        }

        // Remove or extend the background refresh interval to reduce calls
        // const intervalId = setInterval(() => {
        //   setRefreshing(true);
        //   fetchDailyProgress(false)
        //     .finally(() => {
        //       setTimeout(() => setRefreshing(false), 1000);
        //     });
        // }, 60000);

        return () => {
            // clearInterval(intervalId);
        };
    }, [data, loading, error, fetchDailyProgress]);

    // Log whenever the dailyProgressData context changes
    useEffect(() => {
        console.log('[DailyProgressWidget] Context data updated:', {
            loading: dailyProgressData.loading,
            hasData: !!dailyProgressData.data,
            error: dailyProgressData.error
        });
    }, [dailyProgressData]);

    // Use permanent stable card style
    const cardStyle = {
        width: 600,
        padding: 2,
        backgroundColor: "#1e1e1e",
        color: "#fff",
        textAlign: "center",
        height: "330px"
    };

    // Log processed data for debugging
    useEffect(() => {
        console.log('[DailyProgressWidget] Processed chart data:', processedChartData);
    }, [processedChartData]);

    // If we have data OR cached data, show it
    if (data || cachedData) {
        const { chartData = [], isSamsungUser = false } = processedChartData;
        
        return (
            <Card sx={cardStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h3>Progress</h3>
                    {(loading || refreshing) && (
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                ml: 1, 
                                opacity: 0.6,
                                animation: refreshing ? 'fadeInOut 1.5s infinite' : 'none'
                            }}
                        >
                            updating...
                        </Typography>
                    )}
                </Box>
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="120%" height={250}>
                        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 180, left: -50, bottom: 10 }}>
                            <XAxis type="number" tickCount={10} allowDecimals={false} />
                            <YAxis dataKey="label" type="category" tick={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />

                            {/* Queue bars with different shades based on age - colorblind friendly palette */}
                            <Bar dataKey="queue_over_5_days" stackId="queue" fill="#003f5c" barSize={60} name="Queue (>5 days)" />
                            <Bar dataKey="queue_3_5_days" stackId="queue" fill="#7a5195" barSize={60} name="Queue (3-5 days)" />
                            <Bar dataKey="queue_0_3_days" stackId="queue" fill="#ffa600" barSize={60} name="Queue (<3 days)" />
                            
                            {/* Other metrics - colorblind friendly palette */}
                            <Bar dataKey="newToday" fill="#58508d" barSize={60} name="Inflow today" />
                            <Bar dataKey="startedToday" fill="#bc5090" barSize={60} name="Started today" />
                            {isSamsungUser && <Bar dataKey="wwsCount" fill="#2ec4b6" barSize={60} name="WWS" />}
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '250px' 
                    }}>
                        <Typography>
                            No data available for chart. Raw data: {JSON.stringify(data || cachedData)}
                        </Typography>
                    </Box>
                )}
            </Card>
        );
    }

    // Only show loading state when we have no data and no cached data
    if (loading && !cachedData) {
        return (
            <Card sx={cardStyle}>
                <Typography variant="h6">Progress</Typography>
                <Typography variant="body1">Loading...</Typography>
            </Card>
        );
    }

    if (error && !cachedData) {
        return (
            <Card sx={cardStyle}>
                <Typography variant="h6">Progress</Typography>
                <Typography variant="body1" color="error">Error loading data</Typography>
                <Typography variant="body2" color="error">{String(error)}</Typography>
                <Typography variant="body2">Try refreshing the dashboard</Typography>
                <button onClick={() => fetchDailyProgress(true)} style={{ marginTop: 10 }}>Refresh Data</button>
            </Card>
        );
    }

    // Last resort - no data, no cache, not loading
    return (
        <Card sx={cardStyle}>
            <Typography variant="h6">Progress</Typography>
            <Typography variant="body1">Waiting for data...</Typography>
            <button onClick={() => fetchDailyProgress(true)} style={{ marginTop: 10 }}>
                Fetch Data
            </button>
        </Card>
    );
};

// Wrap the component in React.memo to prevent unnecessary rerenders
export default React.memo(DailyProgressWidget);
